import type { ReservationConfig, Course } from '../store/useStore'
import { DEFAULT_BOOKABLE_DAYS_AHEAD, PREPAID_MIN_LEAD_DAYS } from '../pages/StoreDetail/reservationPolicy'

/** 日付の範囲を出すのに要る設定だけ。店の設定が丸ごと無くても組み立てられるように */
export type BookableConfig = Pick<ReservationConfig, 'availableDays' | 'bookableDaysAhead' | 'paymentTiming'>

// #1357 予約可能な範囲を返す。日付の平置きをやめ、月カレンダーに渡す判定にする。
// (チップ平置きは 90 日設定で 91 個並び、選べたものではなくなる)
// #1374 コースを選ぶと受付可能な最短日が変わる。前払い・手付金は承認に最大 48 時間
// かかるため最低 3 日前まで、店がそれ以上に延ばすこともできる (仕込みの都合)。
// 席のみ予約は対象外なので当日でも取れる。
//
// **サーバでも同じ判定をしている** (reservations.py の course_deadline_passed)。
// ここはカレンダーから押せなくするだけで、正しさの担保はサーバ側。
//
// #2026 `config.paymentTiming` には**実際に使われる**支払い方法を渡す (店の保存値ではない)。
// 新規予約は `reservationPaymentRules(store)`、日時変更はその予約自身の支払い方法。
// 保存値を渡すと、現金のみの店 (= 当日払い) にも前払いの最短 3 日前がかかる。
export function leadTimeDays(config: BookableConfig, course: Course | null): number {
  if (!course) return 0  // 席のみ = 制限なし
  const courseDays = course.reservationDeadlineDays ?? 0
  const timing = config.paymentTiming
  return timing === 'prepaid' || timing === 'deposit'
    ? Math.max(courseDays, PREPAID_MIN_LEAD_DAYS)
    : courseDays
}

// #2020 新規予約 (ReservationModal) と日時変更 (ChangeDatetimeModal) が同じ範囲を使う。
// 日時変更が 14 日固定で、店の「受け付ける期間」(最大 180 日) を見ていなかった。
export function bookableRange(config: BookableConfig, course: Course | null) {
  const days = config.bookableDaysAhead ?? DEFAULT_BOOKABLE_DAYS_AHEAD
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const first = new Date(today)
  first.setDate(today.getDate() + leadTimeDays(config, course))
  const last = new Date(today)
  last.setDate(today.getDate() + days)
  const toStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return {
    minDate: toStr(first),
    maxDate: toStr(last),
    isEnabled: (dateStr: string) => {
      const d = new Date(`${dateStr}T00:00:00`)
      if (d < first || d > last) return false
      return config.availableDays.includes(d.getDay())
    },
  }
}
